/** biome-ignore-all lint/a11y/useSemanticElements: card as a region (not sure why) */
import {
  Box,
  Button,
  Card,
  DropdownButton,
  type DropdownMenuItemType,
  Inline,
  omniConfig,
  Skeleton,
  Stack,
  Tag,
  Typography,
  useViewport,
} from '@omnipresentgroup/design-system';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import React from 'react';

export type IntegrationWidgetStatus =
  | 'connected'
  | 'not_connected'
  | 'in_progress';

const LEARN_MORE_BUTTON_LABEL = 'Learn more';
const BETA_TAG_LABEL = 'Beta';

export type IntegrationWidgetChildProps = {
  isLoading?: boolean;
};

export type IntegrationWidgetProps = {
  id: string;
  isLoading: boolean;
  children: React.ReactNode;
};

export const IntegrationWidget = ({
  id,
  isLoading,
  children,
}: IntegrationWidgetProps) => {
  const viewport = useViewport();
  const fullscreen = viewport.isPhone || viewport.isTablet;

  return (
    <Card
      p="24"
      w={fullscreen ? 'auto' : 400}
      role="region"
      aria-label={`region-${id}`}
    >
      <Stack gap="12" css={{ height: '100%' }}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(
              child as React.ReactElement<IntegrationWidgetChildProps>,
              { isLoading },
            );
          }
          return child;
        })}
      </Stack>
    </Card>
  );
};

const WidgetTitle = ({
  id,
  logo,
  title,
}: {
  id: string;
  logo: string | JSX.Element;
  title: string;
}) => (
  <Inline align="center" gap="4">
    {typeof logo === 'string' ? (
      <img
        data-testing="blackout"
        src={logo}
        alt={`logo-${id}`}
        style={{
          width: omniConfig.theme.sizes[40],
        }}
      />
    ) : (
      <Inline h={40} align="center">
        {logo}
      </Inline>
    )}
    <Typography as="span" size="16" weight="medium">
      {title}
    </Typography>
  </Inline>
);

const IntegrationTag = ({
  id,
  isLoading,
  status,
}: {
  id: string;
  isLoading?: boolean;
  status: IntegrationWidgetStatus | JSX.Element;
}) => {
  if (isLoading) {
    return <Skeleton width={80} height={20} />;
  }

  if (typeof status === 'string') {
    return (
      <Tag
        id={`connection-status-tag-${id}`}
        intent={status === 'connected' ? 'success' : 'default'}
      >
        {toSentenceCase(status)}
      </Tag>
    );
  }

  return status;
};

IntegrationWidget.Header = ({
  id,
  title,
  logo,
  status,
  options,
  isLoading,
  isBeta,
}: {
  id: string;
  title: string;
  logo: string | JSX.Element;
  status: IntegrationWidgetStatus | JSX.Element;
  options?: DropdownMenuItemType[];
  isBeta?: boolean;
} & IntegrationWidgetChildProps) => (
  <Inline align="center" justify="space-between" mb="12">
    <WidgetTitle id={id} logo={logo} title={title} />
    <Inline align="center" justify="flex-end">
      {isBeta && (
        <Tag id={`integration-tag-beta-${id}`} intent="info">
          {BETA_TAG_LABEL}
        </Tag>
      )}
      <IntegrationTag id={id} isLoading={isLoading} status={status} />
      {options && <DropdownButton options={options} icon="Dots" />}
    </Inline>
  </Inline>
);

IntegrationWidget.Body = ({
  content,
  tags,
}: {
  content: string | JSX.Element;
  tags: string[];
} & IntegrationWidgetChildProps) => (
  <Stack css={{ height: '100%' }} justify="space-between">
    {typeof content === 'string' ? (
      <Typography as="span" size="14">
        {content}
      </Typography>
    ) : (
      content
    )}
    {tags.map((tag) => (
      <Tag key={tag} id={`integration-tag-${tag}`} outlined>
        {tag}
      </Tag>
    ))}
  </Stack>
);

IntegrationWidget.Footer = ({
  learnMoreHandle,
  actionButtonLabel,
  isLoading,
}: {
  learnMoreHandle: () => void;
  actionButtonLabel?: string;
} & IntegrationWidgetChildProps) => (
  <Box css={{ marginTop: 'auto' }}>
    <Button
      variant="secondary"
      size="small"
      onClick={learnMoreHandle}
      fullWidth
      loading={isLoading}
      disabled={isLoading}
    >
      {actionButtonLabel || LEARN_MORE_BUTTON_LABEL}
    </Button>
  </Box>
);
